import React from "react";
import useSWR from "swr";
import Cookies from "js-cookie";
import { Sprak } from "./enums";
import * as api from "./api";
import { Varsel } from "./Varsel";
import { rensHTML } from "./rensHTML";
import { useApptekstHentBasertPaaNokkel } from "./hooks/useHentdata";

export function createApptekstContext<
  AppteksterType extends { [key: string]: string } = { [key: string]: string }
>() {
  const ApptekstContext = React.createContext<AppteksterType | null>(null);
  type ApptekstProviderType = {
    /** Navnet på appen, slik det er spesifisert i Tekstapp.*/
    appnavn: string[];
    /** Appen din rendres inni ApptekstProvideren, slik at resten av appen har tilgang til apptekstene */
    children: React.ReactChild;
    /** Komponent som vises mens man henter tekster
     *
     * Defaulter til å ikke vise noe som helst mens man venter på data. */
    loadingComponent?: React.ReactElement | null;

    /** Komponent som vises hvis SWR returnerer error
         Defaulter til å vise komponentet <Varsel /> */
    errorComponent?: React.ReactElement | null;
    /** Dersom konsument har lokal fallback for tekster vil disse brukes dersom tekstapp ikke responderer */
    fallbackTekster?: AppteksterType;
  };

  /** Gir tilgang til apptekster for hele applikasjonen din.
   *
   * Denne komponenten bør wrappe hele appen din, slik at hele appen din får
   * tilgang til apptekster.
   *
   * Valgt språk og miljø utledes av henholdsvis `ForceLocale1`-cookien og URLen.
   *
   * @example <caption>Et enkelt eksempel</caption>
   * <ApptekstProvider appnavn="dinside-eksempelapp">
   *   <App />
   * </ApptekstProvider>
   *
   * @example <caption>Med loadingComponent-komponent</caption>
   * <ApptekstProvider
   *   appnavn="dinside-eksempelapp"
   *   loadingComponent={<Spinner />}
   * >
   *   <App />
   * </ApptekstProvider>
   *
   * @example <caption>Med typesikre tekster</caption>
   * <ApptekstProvider<TeksterType> appnavn="dinside-eksempelapp">
   *   <App />
   * </ApptekstProvider>
   */
  function ApptekstProvider({
    appnavn,
    children,
    loadingComponent = null,
    errorComponent = null,
    fallbackTekster,
  }: ApptekstProviderType) {
    const sprak = (Cookies.get("ForceLocale1") as Sprak) || Sprak.NO;
    const { data, error, isValidating } = useSWR<AppteksterType>(
      appnavn.join(","),
      api.hentApptekster,
      {
        revalidateOnFocus: false,
        revalidateOnMount: true,
        revalidateOnReconnect: false,
      },
    );

    if (error) {
      console.error(error);
      if (fallbackTekster) {
        return (
          <ApptekstContext.Provider value={fallbackTekster}>
            {children}
          </ApptekstContext.Provider>
        );
      }
      return errorComponent || <Varsel sprak={sprak} />;
    }

    if (!data || isValidating) {
      return loadingComponent;
    }

    return (
      <ApptekstContext.Provider value={data}>
        {children}
      </ApptekstContext.Provider>
    );
  }

  type MockApptekstProviderProps = {
    children: React.ReactNode;
    value?: Partial<AppteksterType>;
  };
  const MockApptekstProvider: React.FC<MockApptekstProviderProps> = ({
    children,
    value = {},
  }) => {
    return (
      <ApptekstContext.Provider value={value as any}>
        {children}
      </ApptekstContext.Provider>
    );
  };

  /** En hook for å hente ut apptekster
   *
   * Returnerer en funksjon som man kan kalle med en gitt apptekst-verdi.
   * Om teksten ikke finnes, rendres bare apptekst-nøkkelen.
   */
  const useApptekster = () => {
    const contextData = React.useContext(ApptekstContext);

    return React.useCallback(
      (tekstnokkel: keyof AppteksterType, ...args: unknown[]): string => {
        const tekst = contextData && contextData[tekstnokkel];

        if (!tekst) {
          return String(tekstnokkel);
        }

        return kompilerTekstOgRensHTML(tekst, args);
      },
      [contextData],
    );
  };
  return {
    ApptekstProvider,
    useApptekster,
    MockApptekstProvider,
    ApptekstContext,
  };
}

export function useHentApptekstUavhengigAvApp(
  tekstnokkel: string,
  ...args: unknown[]
) {
  const tekst = useApptekstHentBasertPaaNokkel(tekstnokkel);

  if (!tekst) {
    return String(tekstnokkel);
  }

  return kompilerTekstOgRensHTML(tekst, args);
}

function kompilerTekstOgRensHTML<
  AppteksterType extends { [key: string]: string } = { [key: string]: string }
>(tekst: AppteksterType[keyof AppteksterType], args: unknown[]) {
  if (!args.length) {
    return rensHTML(tekst);
  }
  // Denne regexen matcher {slikeMarkorer} / {{slikeMarkorer}} / {{ slikeMarkorer }} i apptekstene.
  // Eksempel: https://regex101.com/r/qeyalU/2
  const markorerRegex = /\{{\s*[\wæøåÆØÅ]+\s*}}|{\s*[\wæøåÆØÅ]+\s*}/g;
  const markorer = tekst.match(markorerRegex);

  if (!markorer?.length) {
    return rensHTML(tekst);
  }

  let kompilertTekst = tekst as string;

  for (let i = 0; i < markorer?.length; i++) {
    if ((args[i] as string) !== "hentUavhengigAvApp") {
      kompilertTekst = kompilertTekst.replace(markorer[i], args[i] as string);
    }
  }
  // rensHTML fjerner html tagger og attributter som ikke er eksplisitt tillatt
  return rensHTML(kompilertTekst);
}
